import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCopyToClipboard } from '@uidotdev/usehooks';

import db from '../lib/db';
import { apiPost } from '../lib/api_v2';
import { currentProfileId, currentShadowId, getMetaTagsSnapshot } from '../lib/meta';
import { escapeRegex } from '../lib/stringUtils';
import { useWorkspacePresenceByWorkspaceId } from '../lib/workspace_presence_store';
import { subscribeWorkspaceIdScope, useWorkspaceIdScope } from '../lib/workspaceIdScope';

import { KeyMasterProvider } from '../contexts/KeyMaster';
import CatchupBrowser from './LogViewerCatchupBrowser';
import LogViewerMediaAttachmentsTab from './LogViewerMediaAttachmentsTab';
import MessageBrowser from './LogViewerMessageBrowser';
import {
  FETCH_DISABLED_CONFIG,
  FETCH_DISABLED_GROUP_LABEL,
  FETCH_DISABLED_TYPES,
  readFetchDisabledFlag,
  writeFetchDisabledFlag,
} from './shared/fetchDisabledConfig';

const ClipboardIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16" {...props}>
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 2.5h4c.552 0 1 .448 1 1V4h1.25c.69 0 1.25.56 1.25 1.25v7.5c0 .69-.56 1.25-1.25 1.25H3.75c-.69 0-1.25-.56-1.25-1.25v-7.5C2.5 4.56 3.06 4 3.75 4H5V3.5c0-.552.448-1 1-1Z"
    />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6.25 4h3.5" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6 2.5c0-.552.448-1 1-1h2c.552 0 1 .448 1 1" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6 7.5h4" />
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6 10h4" />
  </svg>
);

const CheckIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16" {...props}>
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M4.25 8.5 6.5 10.75l5.25-6.5" />
  </svg>
);

const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
};

const formatLogForClipboard = (log) => {
  const lines = [];
  lines.push(`${'='.repeat(20)} ${formatTimestamp(log.timestamp)} / ${log.timestamp} / ${log.level} / ${log.tag} ${'='.repeat(20)}`);
  lines.push(log.message || '');
  if (log.data) {
    const serializedData = typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : String(log.data);
    lines.push(`Data:\n${serializedData}`);
  }
  if (log.stack) lines.push(`Stack:\n${log.stack}`);
  return lines.join('\n');
};

// reusable filters for logs tab
const LogsFilters = ({ searchQuery, setSearchQuery, selectedLevel, setSelectedLevel, uniqueTags, selectedTags, toggleTag }) => (
  <div className="space-y-1.5 border-b border-gray-200 p-2" data-app-region="none">
    {/* search and level filter */}
    <div className="flex items-center gap-2">
      <input
        type="text"
        placeholder="Search logs..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="flex-1 rounded border border-gray-300 px-2 py-0.5 text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
      />
      <div className="flex items-center gap-1">
        <span className="text-2xs text-gray-600">Level:</span>
        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          className="rounded border border-gray-300 px-1 py-0.5 text-2xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
        >
          <option value="all">All</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
          <option value="fatal">Fatal</option>
        </select>
      </div>
    </div>

    {/* tag chips */}
    <div className="flex flex-wrap gap-1">
      <span className="mr-1 text-2xs text-gray-600">Tags:</span>
      {uniqueTags.map((tag) => (
        <button
          key={tag}
          onClick={() => toggleTag(tag)}
          className={`rounded-xs px-1.5 py-0 text-2xs ${
            selectedTags.has(tag) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {tag}
        </button>
      ))}
    </div>
  </div>
);

const TabBar = ({
  isCompact,
  activeTab,
  setActiveTab,
  onClearAll,
  onToggleCompact,
  onCopyLogs,
  hasCopied,
  copyDisabled,
  onClose,
  onReload,
}) => {
  const tabButtonBase = 'text-xs font-medium px-3 py-1';
  const tabActive = 'border-b-2 border-black text-black';
  const tabInactive = 'text-gray-600 hover:text-gray-800';

  return (
    <div className={`flex items-center justify-between border-b border-gray-200`} data-app-region="none">
      <div className="flex">
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Logs"
          onClick={() => setActiveTab('logs')}
          className={`${tabButtonBase} ${activeTab === 'logs' ? tabActive : tabInactive}`}
        >
          L
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Tracked"
          onClick={() => setActiveTab('tracked')}
          className={`${tabButtonBase} ${activeTab === 'tracked' ? tabActive : tabInactive}`}
        >
          T
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Uploads"
          onClick={() => setActiveTab('uploads')}
          className={`${tabButtonBase} ${activeTab === 'uploads' ? tabActive : tabInactive}`}
        >
          U
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="iOS logs"
          onClick={() => setActiveTab('iosLogs')}
          className={`${tabButtonBase} ${activeTab === 'iosLogs' ? tabActive : tabInactive}`}
        >
          I
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Browser"
          onClick={() => setActiveTab('browser')}
          className={`${tabButtonBase} ${activeTab === 'browser' ? tabActive : tabInactive}`}
        >
          B
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Catchup"
          onClick={() => setActiveTab('catchup')}
          className={`${tabButtonBase} ${activeTab === 'catchup' ? tabActive : tabInactive}`}
        >
          C
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Meta tags"
          onClick={() => setActiveTab('meta')}
          className={`${tabButtonBase} ${activeTab === 'meta' ? tabActive : tabInactive}`}
        >
          M
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Presence"
          onClick={() => setActiveTab('presence')}
          className={`${tabButtonBase} ${activeTab === 'presence' ? tabActive : tabInactive}`}
        >
          P
        </button>
      </div>
      <div className="flex items-center gap-2 px-2">
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Clear all"
          onClick={onClearAll}
          className="rounded px-1.5 py-0.5 text-2xs text-gray-600 hover:bg-gray-200"
        >
          <svg className="inline size-3 align-text-bottom" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" strokeWidth="2" />
            <line x1="7" y1="17" x2="17" y2="7" strokeWidth="2" />
          </svg>
        </button>
        {activeTab === 'logs' && (
          <button
            data-controller="tippy"
            data-tippy-theme="mini"
            data-tippy-content="Copy logs"
            onClick={onCopyLogs}
            className={`rounded p-1 ${hasCopied ? 'text-green-600' : 'text-gray-600'} hover:bg-gray-200 disabled:opacity-40`}
            disabled={copyDisabled}
          >
            {hasCopied ? <CheckIcon className="size-3" /> : <ClipboardIcon className="size-3" />}
          </button>
        )}
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Toggle"
          onClick={onToggleCompact}
          className="rounded p-1 text-gray-600 hover:bg-gray-200"
        >
          <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isCompact ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            )}
          </svg>
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Reload"
          onClick={onReload}
          className="rounded p-1 text-gray-600 hover:bg-gray-200"
        >
          <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
        <button
          data-controller="tippy"
          data-tippy-theme="mini"
          data-tippy-content="Close"
          onClick={onClose}
          className="rounded p-1 text-gray-600 hover:bg-gray-200"
        >
          <svg className="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const LogRow = ({ getLevelColor, index, log }) => (
  <div className={`border-b border-gray-100 px-2 py-1 ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
    <div className="flex items-center gap-1">
      <span className="text-3xs whitespace-nowrap text-gray-500">{formatTimestamp(log.timestamp)}</span>
      <span className={`text-3xs font-semibold uppercase ${getLevelColor(log.level)}`}>{log.level}</span>
      <span className="rounded-xs px-1 py-0 text-3xs">{log.tag}</span>
    </div>
    <div className="mt-0.5 text-2xs text-gray-800">{log.message}</div>
    {log.data && (
      <div className="mt-0.5">
        <pre className="overflow-x-auto rounded bg-gray-100 p-1 text-3xs text-gray-600">{JSON.stringify(log.data, null, 2)}</pre>
      </div>
    )}
    {log.stack && (
      <div className="mt-0.5">
        <pre className="overflow-x-auto rounded bg-red-50 p-1 text-3xs text-red-600">{log.stack}</pre>
      </div>
    )}
  </div>
);

const LogsTab = ({ logs, getLevelColor, onFilteredLogsChange }) => {
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [isInitialized, setIsInitialized] = useState(false);

  // load persisted filters on mount
  useEffect(() => {
    const savedSelectedTags = localStorage.getItem('logViewer.selectedTags');
    const savedSearchQuery = localStorage.getItem('logViewer.searchQuery');
    const savedSelectedLevel = localStorage.getItem('logViewer.selectedLevel');
    if (savedSelectedTags) {
      try {
        setSelectedTags(new Set(JSON.parse(savedSelectedTags)));
      } catch {}
    }
    if (savedSearchQuery) setSearchQuery(savedSearchQuery);
    if (savedSelectedLevel) setSelectedLevel(savedSelectedLevel);
    setIsInitialized(true);
  }, []);

  // persist filters
  useEffect(() => {
    if (isInitialized) localStorage.setItem('logViewer.selectedTags', JSON.stringify(Array.from(selectedTags)));
  }, [selectedTags, isInitialized]);
  useEffect(() => {
    if (isInitialized) localStorage.setItem('logViewer.searchQuery', searchQuery);
  }, [searchQuery, isInitialized]);
  useEffect(() => {
    if (isInitialized) localStorage.setItem('logViewer.selectedLevel', selectedLevel);
  }, [selectedLevel, isInitialized]);

  const uniqueTags = useMemo(() => {
    const tagMap = new Map();
    logs.forEach((log) => {
      if (log.tag) tagMap.set(log.tag, true);
    });
    return Array.from(tagMap.keys()).sort();
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (selectedTags.size > 0 && !selectedTags.has(log.tag)) return false;
      if (selectedLevel !== 'all' && log.level !== selectedLevel) return false;
      if (searchQuery) {
        const regex = new RegExp(escapeRegex(searchQuery), 'i');
        let dataString = '';
        if (log.data != null) {
          if (typeof log.data === 'string') dataString = log.data;
          else {
            try {
              dataString = JSON.stringify(log.data, null, 2);
            } catch {
              dataString = String(log.data);
            }
          }
        }
        if (!regex.test(log.message) && !regex.test(log.tag) && !regex.test(dataString)) return false;
      }
      return true;
    });
  }, [logs, selectedTags, searchQuery, selectedLevel]);

  useEffect(() => {
    if (onFilteredLogsChange) onFilteredLogsChange(filteredLogs);
  }, [filteredLogs, onFilteredLogsChange]);

  const toggleTag = useCallback((tag) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) newSet.delete(tag);
      else newSet.add(tag);
      return newSet;
    });
  }, []);
  const renderLog = useCallback(
    (log, index) => <LogRow key={log.id} getLevelColor={getLevelColor} index={index} log={log} />,
    [getLevelColor],
  );

  if (!filteredLogs.length)
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <LogsFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedLevel={selectedLevel}
          setSelectedLevel={setSelectedLevel}
          uniqueTags={uniqueTags}
          selectedTags={selectedTags}
          toggleTag={toggleTag}
        />
        <div className="p-4 text-center text-xs text-gray-500">No logs to display</div>
      </div>
    );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <LogsFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedLevel={selectedLevel}
        setSelectedLevel={setSelectedLevel}
        uniqueTags={uniqueTags}
        selectedTags={selectedTags}
        toggleTag={toggleTag}
      />
      <div className="size-full overflow-y-auto">{filteredLogs.map(renderLog)}</div>
    </div>
  );
};

const TrackedTab = ({ trackedValues }) => {
  const [flashingValues, setFlashingValues] = useState(new Set());
  const previousTrackedValuesRef = useRef(new Map());
  const [fetchDisabledSettings, setFetchDisabledSettings] = useState(() => {
    const initial = {};
    FETCH_DISABLED_TYPES.forEach((type) => {
      initial[type] = readFetchDisabledFlag(type);
    });
    return initial;
  });

  const handleToggleFetchDisabled = useCallback(
    (type) => (event) => {
      const nextValue = event.target.checked;
      writeFetchDisabledFlag(type, nextValue);
      setFetchDisabledSettings((prev) => ({ ...prev, [type]: nextValue }));
      window.dispatchEvent(new CustomEvent('output:reload', { detail: { source: 'log-viewer', reason: 'fetch-disabled-toggle' } }));
    },
    [],
  );

  const trackedValuesArray = useMemo(() => {
    return Array.from(trackedValues.values()).sort((a, b) => a.id.localeCompare(b.id));
  }, [trackedValues]);

  // detect value changes to flash rows
  useEffect(() => {
    const currentTimestamps = new Map();
    trackedValuesArray.forEach((tracked) => {
      currentTimestamps.set(tracked.id, tracked.timestamp);
    });
    const previousTimestamps = previousTrackedValuesRef.current;
    const newFlashingIds = new Set();
    currentTimestamps.forEach((currentTimestamp, id) => {
      const previousTimestamp = previousTimestamps.get(id);
      if (previousTimestamp && currentTimestamp !== previousTimestamp) newFlashingIds.add(id);
    });
    if (newFlashingIds.size > 0) {
      setFlashingValues(newFlashingIds);
      setTimeout(() => setFlashingValues(new Set()), 150);
    }
    previousTrackedValuesRef.current = currentTimestamps;
  }, [trackedValuesArray]);

  return (
    <div className="overflow-y-auto p-2">
      <div className="flex flex-col">
        <div className="mb-3 rounded border border-gray-200 bg-white p-2">
          <div className="mb-1 text-2xs font-semibold text-gray-700">{FETCH_DISABLED_GROUP_LABEL}</div>
          <div className="space-y-1">
            {FETCH_DISABLED_TYPES.map((type) => {
              const config = FETCH_DISABLED_CONFIG[type];
              return (
                <label key={type} className="flex items-center gap-2 text-2xs text-gray-700">
                  <input
                    type="checkbox"
                    className="size-3"
                    checked={!!fetchDisabledSettings[type]}
                    onChange={handleToggleFetchDisabled(type)}
                  />
                  <span>{config.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        {trackedValuesArray.length ? (
          <div className="divide-y divide-gray-100">
            {trackedValuesArray.map((tracked) => (
              <div key={tracked.id} className="flex gap-1 px-1 py-0.5">
                <div className="flex-shrink-0 py-0.5 text-3xs">{tracked.id}</div>
                <div
                  className={`w-full min-w-0 flex-grow overflow-x-auto rounded px-1 py-0.5 text-3xs text-gray-600 ${flashingValues.has(tracked.id) ? 'bg-yellow-100' : ''}`}
                >
                  {typeof tracked.value === 'object' ? JSON.stringify(tracked.value, null, 2) : String(tracked.value)}
                </div>
                <div className="w-10 flex-shrink-0 py-0.5 text-right text-3xs whitespace-nowrap text-gray-500">
                  {formatTimestamp(tracked.timestamp)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-xs text-gray-500">No tracked values</div>
        )}
      </div>
    </div>
  );
};

const BrowserTab = () => (
  <div className="flex h-full flex-col p-4">
    <div className="flex min-h-0 flex-1 flex-col space-y-4">
      <div className="min-h-0 flex-1">
        <MessageBrowser />
      </div>
    </div>
  </div>
);

const CatchupTab = () => (
  <div className="flex h-full flex-col">
    <div className="flex min-h-0 flex-1 flex-col">
      <CatchupBrowser />
    </div>
  </div>
);

const IosLogUploadTab = () => {
  const [file, setFile] = useState(null);
  const [contentType, setContentType] = useState('text/plain');
  const [signedUpload, setSignedUpload] = useState(null);
  const [signerStatus, setSignerStatus] = useState('idle');
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const signerRequest = useMemo(() => {
    if (!file) return null;

    return {
      ios_log_upload: {
        file_name: file.name || 'ios-log.txt',
        content_type: contentType || 'application/octet-stream',
        content_length: file.size,
      },
    };
  }, [contentType, file]);

  const handleFileChange = useCallback((event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
    setSignedUpload(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setError('');
    if (nextFile?.type) setContentType(nextFile.type);
  }, []);

  const requestSignedUpload = useCallback(async () => {
    if (!signerRequest) return;

    setSignerStatus('loading');
    setUploadStatus('idle');
    setUploadProgress(0);
    setError('');

    try {
      const response = await apiPost('/ios_log_uploads', signerRequest);
      const payload = await response.json();

      if (!response.ok || !payload.ok) {
        throw new Error(JSON.stringify(payload, null, 2));
      }

      setSignedUpload(payload.data.ios_log_upload);
      setSignerStatus('success');
    } catch (nextError) {
      setSignerStatus('error');
      setSignedUpload(null);
      setError(nextError?.message || 'signer-failed');
    }
  }, [signerRequest]);

  const uploadSelectedFile = useCallback(async () => {
    if (!file || !signedUpload?.upload_url) return;

    setUploadStatus('loading');
    setUploadProgress(0);
    setError('');

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) setUploadProgress(Math.round((event.loaded / event.total) * 100));
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`upload-failed-${xhr.status}: ${xhr.responseText || xhr.statusText}`));
        });

        xhr.addEventListener('error', () => reject(new Error('upload-network-error')));
        xhr.open(signedUpload.method || 'PUT', signedUpload.upload_url);

        Object.entries(signedUpload.headers || {}).forEach(([name, value]) => {
          if (name.toLowerCase() === 'content-length') return;
          xhr.setRequestHeader(name, value);
        });

        xhr.send(file);
      });

      setUploadProgress(100);
      setUploadStatus('success');
    } catch (nextError) {
      setUploadStatus('error');
      setError(nextError?.message || 'upload-failed');
    }
  }, [file, signedUpload]);

  return (
    <div className="h-full overflow-y-auto p-3" data-app-region="none">
      <div className="space-y-3 text-2xs text-gray-700">
        <div className="space-y-1">
          <label className="block font-semibold text-gray-800">File</label>
          <input type="file" onChange={handleFileChange} className="block w-full text-2xs" />
        </div>

        <div className="space-y-1">
          <label className="block font-semibold text-gray-800">Content type</label>
          <input
            type="text"
            value={contentType}
            onChange={(event) => setContentType(event.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-2xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {file && (
          <div className="rounded border border-gray-200 bg-gray-50 p-2 font-mono text-3xs">
            <div>{file.name}</div>
            <div>{file.size} bytes</div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={requestSignedUpload}
            disabled={!file || signerStatus === 'loading'}
            className="rounded bg-gray-900 px-2 py-1 text-2xs font-semibold text-white disabled:opacity-40"
          >
            {signerStatus === 'loading' ? 'Signing' : 'Get URL'}
          </button>
          <button
            type="button"
            onClick={uploadSelectedFile}
            disabled={!file || !signedUpload || uploadStatus === 'loading'}
            className="rounded bg-blue-600 px-2 py-1 text-2xs font-semibold text-white disabled:opacity-40"
          >
            {uploadStatus === 'loading' ? `Uploading ${uploadProgress}%` : 'Upload'}
          </button>
        </div>

        {(signerStatus === 'success' || uploadStatus === 'success') && (
          <div className="rounded border border-green-200 bg-green-50 p-2 text-2xs text-green-700">
            {uploadStatus === 'success' ? 'Upload complete' : 'Signed URL ready'}
          </div>
        )}

        {error && <pre className="overflow-x-auto rounded border border-red-200 bg-red-50 p-2 text-3xs text-red-700">{error}</pre>}

        {signerRequest && (
          <div className="space-y-1">
            <div className="font-semibold text-gray-800">Signer request</div>
            <pre className="overflow-x-auto rounded bg-gray-100 p-2 text-3xs">{JSON.stringify(signerRequest, null, 2)}</pre>
          </div>
        )}

        {signedUpload && (
          <div className="space-y-1">
            <div className="font-semibold text-gray-800">Signer response</div>
            <pre className="overflow-x-auto rounded bg-gray-100 p-2 text-3xs">{JSON.stringify(signedUpload, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

const PresenceTab = () => {
  const routeWorkspaceId = useWorkspaceIdScope();
  const presenceByWorkspaceId = useWorkspacePresenceByWorkspaceId();
  const ownProfileId = currentProfileId();

  const profileNamesByHashid = useLiveQuery(
    async () => {
      if (!routeWorkspaceId) return new Map();
      const profiles = await db.profiles.where('workspace_id').equals(routeWorkspaceId).toArray();
      return new Map(profiles.map((profile) => [profile.id, profile.full_name]));
    },
    [routeWorkspaceId],
    new Map(),
  );

  const workspaceIds = useMemo(() => Object.keys(presenceByWorkspaceId).sort(), [presenceByWorkspaceId]);

  if (!workspaceIds.length) {
    return <div className="p-4 text-center text-xs text-gray-500">No presence data yet — waiting for first snapshot.</div>;
  }

  return (
    <div className="overflow-y-auto p-2">
      {workspaceIds.map((workspaceId) => {
        const presence = presenceByWorkspaceId[workspaceId] || { records: [], total: 0, version: 0 };
        const isCurrent = workspaceId === routeWorkspaceId;

        return (
          <div key={workspaceId} className="mb-3">
            <div className="mb-1 flex items-center justify-between text-2xs font-semibold text-gray-700">
              <span>
                {isCurrent ? '★ ' : ''}workspace {workspaceId}
              </span>
              <span className="text-3xs text-gray-500">
                v{presence.version ?? 0} · {presence.total ?? 0} present
              </span>
            </div>
            {presence.records?.length ? (
              <div className="divide-y divide-gray-100">
                {presence.records.map((record) => {
                  const recordKey = `${record.participant_type}:${record.participant_id}`;
                  const profileId = record.profile_id || record.participant_id;
                  const name = profileNamesByHashid?.get(profileId);
                  const isSelf = profileId === ownProfileId;
                  const platforms = (record.platforms || []).join(', ');
                  return (
                    <div key={recordKey} className="flex items-center gap-2 px-1 py-0.5 text-3xs">
                      <div className="min-w-0 flex-grow truncate">
                        <span className="font-semibold">{name || profileId}</span>
                        {isSelf && <span className="ml-1 text-gray-400">(you)</span>}
                        {name && <span className="ml-1 font-mono text-gray-400">{profileId}</span>}
                      </div>
                      <div className="whitespace-nowrap text-gray-600">
                        ×{record.session_count}
                        {platforms && ` · ${platforms}`}
                      </div>
                      <div className="w-16 text-right whitespace-nowrap text-gray-500">
                        {record.last_seen_at ? formatTimestamp(record.last_seen_at * 1000) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-1 text-3xs text-gray-500">No participants present</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MetaTab = () => {
  const [, setScopeVersion] = useState(0);

  useEffect(() => {
    return subscribeWorkspaceIdScope(() => {
      setScopeVersion((version) => version + 1);
    });
  }, []);

  const metaTags = getMetaTagsSnapshot();

  if (!metaTags.length) {
    return <div className="p-4 text-center text-xs text-gray-500">No meta tags found</div>;
  }

  return (
    <div className="overflow-y-auto p-2">
      <div className="divide-y divide-gray-100">
        {metaTags.map(({ key, value }) => (
          <div key={key} className="flex gap-2 px-1 py-0.5 text-3xs">
            <div className="flex-shrink-0 font-semibold text-gray-700">{key}</div>
            <div className="min-w-0 flex-grow truncate text-gray-600">{value ?? <span className="text-gray-400">null</span>}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LogViewer = ({ isVisible = false, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [trackedValues, setTrackedValues] = useState(new Map());
  const maxLogs = useRef(1000);

  const [visibleLogs, setVisibleLogs] = useState([]);
  const [hasCopied, setHasCopied] = useState(false);
  const copyResetTimeoutRef = useRef(null);
  const [, copyToClipboard] = useCopyToClipboard();

  // Initialize state from localStorage or defaults
  const [activeTab, setActiveTab] = useState('logs');
  const [isCompact, setIsCompact] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load persisted shared state from localStorage on mount
  useEffect(() => {
    const savedActiveTab = localStorage.getItem('logViewer.activeTab');
    const savedIsCompact = localStorage.getItem('logViewer.isCompact');

    if (savedActiveTab) setActiveTab(savedActiveTab);

    if (savedIsCompact !== null) setIsCompact(savedIsCompact === 'true');
    setIsInitialized(true);
  }, []);

  // Persist activeTab to localStorage (only after initialization)
  useEffect(() => {
    if (isInitialized) localStorage.setItem('logViewer.activeTab', activeTab);
  }, [activeTab, isInitialized]);

  // Persist isCompact to localStorage (only after initialization)
  useEffect(() => {
    if (isInitialized) localStorage.setItem('logViewer.isCompact', isCompact.toString());
  }, [isCompact, isInitialized]);

  // Set up event listeners for logger events
  useEffect(() => {
    const handleLogEvent = (event) => {
      const logEntry = event.detail;
      setLogs((prevLogs) => {
        // Add new log and maintain max size
        const newLogs = [logEntry, ...prevLogs];
        if (newLogs.length > maxLogs.current) {
          return newLogs.slice(0, maxLogs.current);
        }
        return newLogs;
      });
    };

    const handleTrackEvent = (event) => {
      const { id, value, timestamp } = event.detail;
      setTrackedValues((prevValues) => {
        const newValues = new Map(prevValues);
        newValues.set(id, { id, value, timestamp });
        return newValues;
      });
    };

    window.addEventListener('logger:log', handleLogEvent);
    window.addEventListener('logger:track', handleTrackEvent);

    return () => {
      window.removeEventListener('logger:log', handleLogEvent);
      window.removeEventListener('logger:track', handleTrackEvent);
    };
  }, []);

  const clearAll = useCallback(() => {
    setLogs([]);
    setTrackedValues(new Map());
  }, []);

  const toggleCompact = useCallback(() => {
    setIsCompact(!isCompact);
  }, [isCompact]);

  const reloadWindow = useCallback(() => {
    window.dispatchEvent(new CustomEvent('output:reload', { detail: { source: 'log-viewer', reason: 'manual-reload-button' } }));
  }, []);

  const copyLogsToClipboard = useCallback(() => {
    if (!visibleLogs.length) return;
    let clipboardText = visibleLogs.map((log) => formatLogForClipboard(log)).join('\n\n');
    clipboardText += `\n\n\n\nwindow.location.href: ${window.location.href}\n\ncurrentProfileId: ${currentProfileId()}\n\ncurrentShadowId: ${currentShadowId()}`;
    try {
      copyToClipboard(clipboardText);
      setHasCopied(true);
    } catch (error) {
      console.error('Failed to copy logs', error);
      setHasCopied(false);
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
        copyResetTimeoutRef.current = null;
      }
      return;
    }
    if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = setTimeout(() => {
      setHasCopied(false);
      copyResetTimeoutRef.current = null;
    }, 1000);
  }, [copyToClipboard, visibleLogs]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
        copyResetTimeoutRef.current = null;
      }
    };
  }, []);

  if (!isVisible) return null;

  const getLevelColor = (level) => {
    switch (level) {
      case 'debug':
        return 'text-gray-500';
      case 'info':
        return 'text-blue-600';
      case 'warn':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      case 'fatal':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  // unified render helper for tab content
  const renderContent = () => {
    switch (activeTab) {
      case 'logs':
        return <LogsTab logs={logs} getLevelColor={getLevelColor} onFilteredLogsChange={setVisibleLogs} />;
      case 'tracked':
        return <TrackedTab trackedValues={trackedValues} />;
      case 'uploads':
        return <LogViewerMediaAttachmentsTab />;
      case 'iosLogs':
        return <IosLogUploadTab />;
      case 'browser':
        return <BrowserTab />;
      case 'catchup':
        return <CatchupTab />;
      case 'meta':
        return <MetaTab />;
      case 'presence':
        return <PresenceTab />;
      default:
        return null;
    }
  };

  // unified layout - adjust wrapper classes based on compact state
  const outerWrapperClass = isCompact
    ? 'fixed top-0 right-0 z-50 w-80 font-mono sm:top-auto sm:bottom-0'
    : 'fixed top-0 right-0 bottom-0 left-0 w-full font-mono sm:left-auto sm:w-112';

  const containerClass = isCompact
    ? 'flex sm:h-128 h-96 flex-col border border-gray-200 bg-white shadow-xl'
    : 'flex h-full flex-col border-l border-gray-200 bg-white shadow-xl';

  return (
    <KeyMasterProvider>
      <div className={outerWrapperClass}>
        <div className={containerClass}>
          <div className="flex flex-1 flex-col overflow-hidden">
            <TabBar
              isCompact={isCompact}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onClearAll={clearAll}
              onToggleCompact={toggleCompact}
              onCopyLogs={copyLogsToClipboard}
              hasCopied={hasCopied}
              copyDisabled={!visibleLogs.length}
              onClose={onClose}
              onReload={reloadWindow}
            />

            <div className="flex-1 overflow-hidden">{renderContent()}</div>
          </div>
        </div>
      </div>
    </KeyMasterProvider>
  );
};

export default LogViewer;
